/**
 * axios-resource v0.1.3
 * (c) 2018 Emile Bergeron <ber.emile@prismalstudio.com>
 * @license MIT
 */
import axios from 'axios';
import objectAssign from 'object-assign';

function isObj(obj) {
    return obj !== null && typeof (obj) === 'object';
}

/**
 * @description get nested properties from a given
 * object using dot notation
 *
 * @method prop
 *
 * @param  {Object} obj
 * @param  {Array|string} path
 *
 * @return {*}
 */
function prop(obj, path) {
    if (typeof path === 'string') { path = path.split('.'); }
    if (!isObj(obj) || !path) {
        return obj;
    }

    for (var i = 0; i < path.length; ++i) {
        var p = path[i];
        if (!Object.prototype.hasOwnProperty.call(obj, p)) { return undefined; }
        obj = obj[path[i]];
    }
    return obj;
}

var regex = /{{2}(.+?)}{2}/g;

/**
 * Parses a given template string and
 * replace dynamic placeholders with actual data.
 *
 * @param {string} template
 * @param {Object} [data]
 * @param {Object} [options]
 */
function template (template, data, options) {
    if ( options === void 0 ) options = {};

    if (typeof template !== 'string') { return ''; }
    return template.replace(options.regex || regex, function (match, key) {
        var value = prop(data, key.trim());
        if (value == null) {
            if (options.throwOnUndefined) {
                throw new ReferenceError(("Missing value for " + match));
            }
            return options.skipUndefined ? match : '';
        }
        return value;
    });
}

var Resource = function Resource(options) {
    if ( options === void 0 ) options = {};

    var statics = this.constructor;
    this.idAttribute = options.idAttribute || statics.ID_ATTRIBUTE || 'id';
    this.http = options.http || axios;
    this._url = options.url || statics.URL || '';
};

/**
 * Override this to dynamically build an URL.
 * @returns {string}
 */
Resource.prototype.url = function url (keys) {
    return template(this._url, keys || {}, { regex: /{(.*?)}/g });
};

/**
 * Applies the id automatically within the keys.
 * @param {string} [id]
 * @param {Object} [options]
 * @returns {{keys: {}}}
 */
Resource.prototype.applyUrlKeys = function applyUrlKeys (id, options) {
        var obj;

    if (!id) { return options; }
    var keys = (options || {}).keys;
    return objectAssign({}, options, {
        keys: objectAssign(( obj = {}, obj[this.idAttribute] = id, obj), keys),
    });
};

/**
 * @param {string} id
 * @param {Object} [options]
 * @returns {*}
 */
Resource.prototype.fetch = function fetch (id, options) {
    return this.sync('get', null, this.applyUrlKeys(id, options));
};

/**
 * @param {Object} attrs
 * @param {Object} [options]
 * @returns {PromiseLike<T>|Promise<T>|*}
 */
Resource.prototype.create = function create (attrs, options) {
    return this.sync('post', attrs, options);
};

/**
 * @param {string} id
 * @param {Object} attrs
 * @param {Object} [options]
 * @returns {PromiseLike<T>|Promise<T>|*}
 */
Resource.prototype.patch = function patch (id, attrs, options) {
    return this.sync('patch', attrs, this.applyUrlKeys(id, options));
};

/**
 * @param {string} id
 * @param {Object} [options]
 * @returns {PromiseLike<T>|Promise<T>|*}
 */
Resource.prototype.delete = function delete$1 (id, options) {
    return this.sync('delete', null, this.applyUrlKeys(id, options));
};

/**
 * The function is passed the raw response object, and should return the attributes hash.
 * @param {Object} response
 * @returns {Object}
 */
Resource.prototype.parse = function parse (response) {
    return response;
};

/**
 * Hook for every request type on a resource.
 * @param {string} method
 * @param {Object|null} [data]
 * @param {Object} [options]
 * @returns {PromiseLike<T>|Promise<T>|*}
 */
Resource.prototype.sync = function sync (method, data, options) {
        var this$1 = this;
        if ( options === void 0 ) options = {};

    return this.http(objectAssign({
        url: this.url(options.keys),
        method: method,
        data: data,
    }, options)).then(function (response) { return this$1.parse(response); });
};

Resource.version = '0.1.3';

export default Resource;
export { Resource };



//////////////////
// WEBPACK FOOTER
// ./node_modules/axios-resource/dist/axios-resource.esm.js
// module id = 17
// module chunks = 0